import { execute } from '@/graphql/execute';
import { BillingInterval, SubscriptionPricingQuery, SubscriptionTier } from '@/graphql/graphql';
import { subscriptionPricingQuery } from '@/queries/subscription.query';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

export type PricingTier = SubscriptionPricingQuery['subscriptionPricing']['tiers'][0];
export type GraduatedTier = PricingTier['storage']['monthly'][0];

export const usePricing = () => {
  const { data, isLoading, error } = useQuery({
    queryKey: ['subscriptionPricing'],
    queryFn: () => execute(subscriptionPricingQuery),
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
  });

  const pricing = data?.subscriptionPricing;

  /**
   * Calculate the total price for a given number of storage blocks
   * Uses graduated pricing tiers
   */
  const calculateStoragePrice = useMemo(() => {
    return (tier: SubscriptionTier, blocks: number, interval: BillingInterval): number => {
      if (!pricing) return 0;

      const tierPricing = pricing.tiers.find((t) => t.tier === tier);
      if (!tierPricing) return 0;

      const tiers = interval === 'MONTHLY' ? tierPricing.storage.monthly : tierPricing.storage.annual;
      if (!tiers.length) return 0;

      let totalAmount = 0;
      let remainingBlocks = blocks;
      let previousUpTo = 0;

      for (const graduatedTier of tiers) {
        const tierUpTo = graduatedTier.upTo ?? Infinity;
        const blocksInThisTier = Math.min(remainingBlocks, tierUpTo - previousUpTo);

        if (blocksInThisTier > 0) {
          totalAmount += blocksInThisTier * graduatedTier.unitAmount;
          remainingBlocks -= blocksInThisTier;
        }

        previousUpTo = tierUpTo;
        if (remainingBlocks <= 0) break;
      }

      return totalAmount;
    };
  }, [pricing]);

  /**
   * Get the price per block at a specific quantity (for display)
   */
  const getPricePerBlock = useMemo(() => {
    return (tier: SubscriptionTier, blocks: number, interval: BillingInterval): number => {
      if (!pricing) return 0;

      const tierPricing = pricing.tiers.find((t) => t.tier === tier);
      if (!tierPricing) return 0;

      const tiers = interval === 'MONTHLY' ? tierPricing.storage.monthly : tierPricing.storage.annual;
      if (!tiers.length) return 0;

      // Find which tier this block count falls into
      for (const graduatedTier of tiers) {
        const tierUpTo = graduatedTier.upTo ?? Infinity;
        if (blocks <= tierUpTo) {
          return graduatedTier.unitAmount;
        }
      }

      // Return last tier price if beyond all tiers
      return tiers[tiers.length - 1].unitAmount;
    };
  }, [pricing]);

  /**
   * Get the starting price for a tier (first block price)
   * If currentBlocks is provided, returns the price at that quantity level
   */
  const getStartingPrice = useMemo(() => {
    return (tier: SubscriptionTier, interval: BillingInterval, currentBlocks?: number): number => {
      if (!pricing) return 0;

      const tierPricing = pricing.tiers.find((t) => t.tier === tier);
      if (!tierPricing) return 0;

      const tiers = interval === 'MONTHLY' ? tierPricing.storage.monthly : tierPricing.storage.annual;
      if (!tiers.length) return 0;

      // If currentBlocks provided, find the price at that quantity
      if (currentBlocks && currentBlocks > 0) {
        for (const graduatedTier of tiers) {
          const tierUpTo = graduatedTier.upTo ?? Infinity;
          if (currentBlocks <= tierUpTo) {
            return graduatedTier.unitAmount;
          }
        }
        // Return last tier price if beyond all tiers
        return tiers[tiers.length - 1].unitAmount;
      }

      // Default: return first tier price
      return tiers[0]?.unitAmount ?? 0;
    };
  }, [pricing]);

  /**
   * Format price in cents to display string
   */
  const formatPrice = (amountCents: number, showCents = true): string => {
    const amount = amountCents / 100;
    if (showCents) {
      return `€${amount.toFixed(0)}`;
    }
    return `€${Math.round(amount)}`;
  };

  return {
    pricing,
    isLoading,
    error,
    calculateStoragePrice,
    getPricePerBlock,
    getStartingPrice,
    formatPrice,
    currency: pricing?.currency ?? 'eur',
  };
};
